import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { CardModule } from 'primeng/card';
import { DatePickerModule } from 'primeng/datepicker';
import { SelectButtonModule } from 'primeng/selectbutton';
import { TableModule } from 'primeng/table';
import {
  SimpleBarChartCardComponent,
  SimpleBarChartData,
} from '../../shared/components/simple-bar-chart-card/simple-bar-chart-card.component';
import {
  DynamicLineSeries,
  LineChartCardComponent,
  LineChartData,
} from '../../shared/components/line-chart-card/line-chart-card.component';
import {
  MixedChartCardComponent,
  MixedChartData,
} from '../../shared/components/mixed-chart-card/mixed-chart-card.component';
import { TickerCardComponent } from '../../shared/components/ticker-card/ticker-card.component';
import { TimeGroupBy } from '../../shared/models/visit-statistics.model';
import { VisitStatisticsService } from '../../shared/services/visit-statistics.service';
import { colors } from '../../theme-preset';

@Component({
  selector: 'app-dashboard-page',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    CardModule,
    DatePickerModule,
    SelectButtonModule,
    TableModule,
    TranslateModule,
    TickerCardComponent,
    SimpleBarChartCardComponent,
    LineChartCardComponent,
    MixedChartCardComponent,
  ],
  providers: [],
  templateUrl: './dashboard-page.component.html',
  styleUrl: './dashboard-page.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DashboardPageComponent implements OnInit {
  #statsService = inject(VisitStatisticsService);

  readonly avgRoutesPerVisit = this.#statsService.avgRoutesPerVisit;
  readonly totalVisits = this.#statsService.totalVisits;
  readonly avgTimePerRoute = this.#statsService.avgTimePerRoute;
  readonly loading = this.#statsService.loading;

  groupByOptions = [
    { label: 'Pro Tag', value: 'day' },
    { label: 'Pro Stunde', value: 'hour' },
  ];

  runsGroupBy = signal<TimeGroupBy>('day');
  visitorsGroupBy = signal<TimeGroupBy>('day');

  rangeDates: Date[] = [
    (() => {
      const date = new Date();
      date.setDate(date.getDate() - 30);
      return date;
    })(),
    new Date(),
  ];

  // --- Data for HorizontalBarChartCardComponent ---
  popularRoutesData = computed<SimpleBarChartData[]>(() =>
    this.#statsService.popularRoutes().map((r) => ({
      category: r.route_name,
      value: r.total_runs,
    })),
  );

  // --- Data for LineChartCardComponent: Durchläufe pro Route ---
  runsTimelineData = computed<LineChartData[]>(() =>
    this.#statsService.runsTimeline().map((e) => ({
      category: e.label,
      ...e.routes,
    })),
  );

  runsTimelineSeries = computed<DynamicLineSeries[]>(() => {
    const timeline = this.#statsService.runsTimeline();
    const allRoutes = [
      ...new Set(timeline.flatMap((e) => Object.keys(e.routes))),
    ];
    return allRoutes.map((routeName, i) => ({
      valueField: routeName,
      argumentField: 'category',
      name: routeName,
      color: colors.palette[i % colors.palette.length],
    }));
  });

  // --- Data for LineChartCardComponent: Besucher ---
  visitorsTimelineData = computed<LineChartData[]>(() =>
    this.#statsService.visitorsTimeline().map((e) => ({
      category: e.label,
      visitors: e.visitors,
    })),
  );

  visitorsTimelineSeries: DynamicLineSeries[] = [
    {
      valueField: 'visitors',
      argumentField: 'category',
      name: 'Besucher',
      color: colors.palette[0],
    },
  ];

  // --- Data for MixedChartCardComponent: Durchläufe pro Stunde ---
  runsPerHourData = computed<MixedChartData[]>(() => {
    const data = this.#statsService.runsPerHour();
    const allHours = Array.from({ length: 24 }, (_, i) => i);
    const dataMap = new Map(data.map((d) => [d.hour, d.total_runs]));

    const from = this.rangeDates[0];
    const to = this.rangeDates[1];
    const distinctDays = Math.max(
      1,
      Math.ceil(
        (to.getTime() - from.getTime()) / (1000 * 60 * 60 * 24),
      ),
    );

    return allHours.map((h) => {
      const total = dataMap.get(h) ?? 0;
      return {
        category: `${h}:00`,
        valueBar: total,
        valueLine: Math.round((total / distinctDays) * 10) / 10,
      };
    });
  });

  ngOnInit() {
    this.loadData();
  }

  loadData() {
    if (this.rangeDates[0] && this.rangeDates[1]) {
      this.#statsService.load(
        this.rangeDates[0],
        this.rangeDates[1],
        this.runsGroupBy(),
        this.visitorsGroupBy(),
      );
    }
  }

  onDateSelect() {
    if (this.rangeDates[0] && this.rangeDates[1]) {
      this.loadData();
    }
  }

  onRunsGroupByChange() {
    this.loadData();
  }

  onVisitorsGroupByChange() {
    this.loadData();
  }
}
